import "./ContainerGroupComponent.scss";
import { FC } from "react";

import c from "classnames";
import { kebabCase } from "lodash-es";

import { DynamicFormGroupComponentProps } from "..";
import { ContainerAttributes } from "../../../../config";

/**
 * Props for the ContainerGroupComponent.
 * Extends generic dynamic form group props and includes container-specific attributes.
 */
interface IProps extends DynamicFormGroupComponentProps {
  attributesSet: ContainerAttributes;
}

/**
 * A layout container that wraps form group children and applies
 * styling such as padding, background color, margin, border radius, and shadow.
 *
 * @param children - The form fields to render inside the container.
 * @param attributesSet - Visual and layout configuration for the container.
 */
export const ContainerGroupComponent: FC<IProps> = ({ children, attributesSet }) => {
  const { backgroundColor, margin, verticalPadding, horizontalPadding, cornerRadius, shadow } =
    attributesSet ?? {};

  // Generate a CSS class for the background color using kebab-case conversion.
  const colorClassName = `bgcolor-${kebabCase(backgroundColor ?? "transparent")}`;

  const paddingStyles = {
    ...(verticalPadding !== null && {
      paddingTop: `${verticalPadding}px`,
      paddingBottom: `${verticalPadding}px`,
    }),
    ...(horizontalPadding !== null && {
      paddingLeft: `${horizontalPadding}px`,
      paddingRight: `${horizontalPadding}px`,
    }),
  };

  const normalizedMargin = margin != null ? `${margin}px` : undefined;
  const normalizedBorderRadius = cornerRadius !== null ? `${cornerRadius}px` : undefined;

  // Check if children is empty and return null if it is
  // Otherwise render the container div with the composed class names and inline styles.
  if (!children || (Array.isArray(children) && children.length === 0)) {
    return null;
  }

  return (
    <div
      className={c("dynamic-form-group", "form-group--container", colorClassName, {
        "form-group--with-shadow": shadow ?? false,
      })}
      style={{
        margin: normalizedMargin,
        borderRadius: normalizedBorderRadius,
        ...paddingStyles,
      }}>
      {children}
    </div>
  );
};
